"use client";

import { useEffect, useState } from "react";
import type { DocumentationTableOfContentsItem } from "@/utils/content/documentation/richText";

export function DocumentationTableOfContents({
  items,
  label,
}: {
  items: DocumentationTableOfContentsItem[];
  label: string;
}) {
  const [activeId, setActiveId] = useState(items[0]?.id);

  useEffect(() => {
    if (items.length === 0) {
      return;
    }

    const updateActiveHeading = () => {
      const headings = items
        .map((item) => document.getElementById(item.id))
        .filter((heading): heading is HTMLElement => heading !== null);
      const current = headings
        .filter((heading) => heading.getBoundingClientRect().top <= 150)
        .at(-1);

      setActiveId(current?.id ?? headings[0]?.id);
    };

    updateActiveHeading();
    window.addEventListener("scroll", updateActiveHeading, { passive: true });
    window.addEventListener("resize", updateActiveHeading);

    return () => {
      window.removeEventListener("scroll", updateActiveHeading);
      window.removeEventListener("resize", updateActiveHeading);
    };
  }, [items]);

  if (items.length === 0) {
    return null;
  }

  return (
    <aside className="hidden w-64 flex-none xl:block">
      <nav
        aria-labelledby="documentation-on-this-page"
        className="sticky top-32 max-h-[calc(100vh-9rem)] overflow-y-auto py-16 pl-10"
      >
        <h2
          className="text-sm font-semibold text-zinc-950"
          id="documentation-on-this-page"
        >
          {label}
        </h2>
        <ol className="mt-4 space-y-3 border-l border-zinc-200 text-sm">
          {items.map((item) => (
            <li className={item.level === 3 ? "pl-4" : ""} key={item.id}>
              <a
                aria-current={activeId === item.id ? "location" : undefined}
                className={[
                  "-ml-px block border-l pl-4 leading-5 transition-colors",
                  activeId === item.id
                    ? "border-grass-500 font-semibold text-grass-700"
                    : "border-transparent text-zinc-500 hover:text-zinc-900",
                ].join(" ")}
                href={`#${item.id}`}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  );
}
